import * as CashFlowDomain from "./cash-flow-domain"
import * as LoanDomain from "./loan-domain"
import * as ObservationDomain from "./observation-domain"
import * as PpiDomain from "./ppi-domain"
import * as ScenarioDomain from "./scenario-domain"
import * as Model from "../../model"

/**
 * Calcule les recettes ou dépenses de fonctionnement du scénario passé en
 * paramètre pour l'année passée en paramètre en fonction de la valeur de
 * `receipts`.
 * @param localAuthority
 * @param scenario
 * @param ppi
 * @param loanDecompositions
 * @param year
 * @param receipts
 */
export function calculateBudget(
    localAuthority: Model.LocalAuthority,
    scenario: Model.Scenario,
    ppi: Model.Ppi,
    loanDecompositions: Model.LoanDecomposition[],
    year: number,
    receipts: boolean
): number {
    return localAuthority.accounts.reduce((budget, laca) => {
        // Si le compte est de la section investissement, il est exclu
        if (!laca.account.operation) return budget

        // Si le sens est en recette mais pas le compte, il est exclu
        if (receipts && !laca.account.receipts) return budget

        // Si le sens est en dépense mais pas le compte, il est exclu
        if (!receipts && !laca.account.expense) return budget

        const accountAmount = findAccountAmount(
            localAuthority,
            laca.account,
            laca.chapter,
            scenario,
            ppi,
            loanDecompositions,
            receipts,
            year
        )

        return budget + accountAmount
    }, 0)
}

/**
 * Calcule le nombre d'années de désendettement de la CAF pour l'année passée en
 * paramètre.
 * @param localAuthority
 * @param scenario
 * @param ppi
 * @param loanDecompositions
 * @param year
 */
export function calculateCashFlowDeleveragingYears(
    localAuthority: Model.LocalAuthority,
    scenario: Model.Scenario,
    ppi: Model.Ppi,
    loanDecompositions: Model.LoanDecomposition[],
    year: number
): number | null {
    const remainingCapital = LoanDomain.getTotalYearRemainingCapital(
        loanDecompositions, year)

    const grossCashFlow = calculateGrossCashFlow(
        localAuthority, scenario, ppi, loanDecompositions, year)

    return grossCashFlow ? remainingCapital / grossCashFlow : null
}

/**
 * Calcule les dépenses de fonctionnement du scénario passé en paramètre pour
 * l'année passée en paramètre.
 * @param localAuthority
 * @param scenario
 * @param ppi
 * @param loanDecompositions
 * @param year
 */
export function calculateExpense(
    localAuthority: Model.LocalAuthority,
    scenario: Model.Scenario,
    ppi: Model.Ppi,
    loanDecompositions: Model.LoanDecomposition[],
    year: number
): number {
    return calculateBudget(
        localAuthority,
        scenario,
        ppi,
        loanDecompositions,
        year,
        false
    )
}

export function calculateOperationsTotalProspective(
    year: number,
    la: Model.LocalAuthority,
    ppi: Model.Ppi,
    receipts: boolean,
    scenario: Model.Scenario,
    loans: Model.Loan[]
): number {
    let result = 0
    let accounts = la.accounts
    const loanDecompositions = LoanDomain.getDecompositions(loans)
    const interests = !receipts && loans ? LoanDomain.getTotalInterestByYear(loans, year) : 0
    if (receipts !== undefined && receipts !== null) {
        accounts = accounts.filter(laca => (laca.account.operation && ((receipts && laca.account.receipts) || (!receipts && laca.account.expense))))
    }
    accounts.map(laca => {
        if ("66111" === laca.account.number) {
            result += interests
        } else {
            result += findAccountAmount(la, laca.account, laca.chapter, scenario, ppi, loanDecompositions, receipts, year)
        }
    })

    return result
}

/**
 * Calcule les recettes de fonctionnement du scénario passé en paramètre pour
 * l'année passée en paramètre.
 * @param localAuthority
 * @param scenario
 * @param ppi
 * @param loanDecompsitions
 * @param year
 */
export function calculateReceipts(
    localAuthority: Model.LocalAuthority,
    scenario: Model.Scenario,
    ppi: Model.Ppi,
    loanDecompsitions: Model.LoanDecomposition[],
    year: number
): number {
    return calculateBudget(
        localAuthority,
        scenario,
        ppi,
        loanDecompsitions,
        year,
        true
    )
}

/**
 * @deprecated
 * @see CashFlowDomain.calculateGrossCashFlow Calcule la capacité
 * d'autofinancement brute rétrospective et/ou prospective.
 *
 * Calcule la CAF brute prospective pour l'année passée en paramètre.
 * @param localAuthority
 * @param scenario
 * @param ppi
 * @param loanDecompositions
 * @param year
 */
export function calculateGrossCashFlow(
    localAuthority: Model.LocalAuthority,
    scenario: Model.Scenario,
    ppi: Model.Ppi,
    loanDecompositions: Model.LoanDecomposition[],
    year: number
): number {
    return localAuthority.accounts.reduce((grossCashFlow, laca) => {
        if (!laca.account.operation)
            return grossCashFlow

        if (CashFlowDomain.isGrossCashFlowAccountToAdd(laca.account))
            return grossCashFlow

        if (CashFlowDomain.isGrossCashFlowAccountToSubtract(laca.account))
            return grossCashFlow

        const receipts = laca.account.receipts
            ? findAccountAmount(
                localAuthority,
                laca.account,
                laca.chapter,
                scenario,
                ppi,
                loanDecompositions,
                true,
                year
            )
            : 0

        const expense = laca.account.expense
            ? findAccountAmount(
                localAuthority,
                laca.account,
                laca.chapter,
                scenario,
                ppi,
                loanDecompositions,
                false,
                year
            )
            : 0

        grossCashFlow -= expense
        grossCashFlow += receipts

        return grossCashFlow
    }, 0)
}

/**
 * Calcule le résultat de fonctionnement prospectif de l'année passées en
 * paramètres.
 * @param localAuthority
 * @param scenario
 * @param ppi
 * @param loanDecompsitions
 * @param year
 */
export function calculateResult(
    localAuthority: Model.LocalAuthority,
    scenario: Model.Scenario,
    ppi: Model.Ppi,
    loanDecompsitions: Model.LoanDecomposition[],
    year: number
): number {
    const receipts = calculateReceipts(
        localAuthority,
        scenario,
        ppi,
        loanDecompsitions,
        year
    )
    const expense = calculateExpense(
        localAuthority,
        scenario,
        ppi,
        loanDecompsitions,
        year
    )

    return receipts - expense
}

/**
 * Trouve le montant non pondéré du compte défini par les paramètres, pour
 * l'année passée en paramètre.
 *
 * - Si l'année passée en paramètre est inférieure à celle du scénario, le
 * montant retourné est le constat rétrospectif de l'année passée en paramètre.
 * - Si le numéro du compte est "66111", le montant retourné est le total des
 * intérêts des emprunts passés en paramètre.
 * - Si une année de scénario existe pour l'année passée en paramètre :
 * 	- Si un compte existe dans cette année de scénario et que sa valeur est
 * définie en pourcentage (`scenarioYearAccount.percentile === true`), le
 * montant retourné est celui du compte de l'année précédente pondéré par la
 * valeur du premier.
 * 	- Si un compte existe dans l'année de scénario correspondante et que sa
 * valeur est définie en absolu (`scenarioYearAccount.percentile === false`), le
 * montant retourné est celui du compte.
 * 	- Sinon le montant retourné est celui de l'année précédente, pondéré ou non
 * selon les comptes ou chapitres du scénario {@link weightAmount}.
 * - Sinon les charges et/ou recettes induites sont retournées s'il existe des
 * projets induits pour cette année.
 *
 * @param la
 * @param acc
 * @param ch
 * @param sc
 * @param ppi
 * @param loans
 * @param receipts
 * @param year
 */
export function findAccountAmount(
    la: Model.LocalAuthority,
    acc: Model.Account,
    ch: Model.Chapter,
    sc: Model.Scenario,
    ppi: Model.Ppi,
    loans: Model.LoanDecomposition[],
    receipts: boolean,
    year: number
): number {
    const induced = PpiDomain.ppiHasInducedProjects(ppi, acc, year)
        ? PpiDomain.calculateInduced(ppi, acc, year, receipts) : 0

    if (sc && year < sc.year) {
        const observation = ObservationDomain.findObservationByAccountId(
            la.observations, acc.id, receipts, year)

        return (observation ? observation.amount : 0) + induced
    }

    if (!receipts && "66111" === acc.number)
        return LoanDomain.getTotalYearInterest(loans, year)

    if (!sc) return induced;

    const scenarioYear = ScenarioDomain.findScenarioYear(sc.years, year)
    const prevAmount = findAccountAmount(
        la, acc, ch, sc, ppi, loans, receipts, year - 1)
    const weightedAmount = ScenarioDomain.weightAmount(
        prevAmount, sc, acc, ch, receipts)

    if (scenarioYear) {
        const scenarioYearAccount = ScenarioDomain
            .findScenarioYearAccountByAccountId(
                scenarioYear.accounts, acc.id, receipts)

        if (scenarioYearAccount && scenarioYearAccount.percentile)
            return prevAmount * (1 + scenarioYearAccount.amount / 100) + induced

        if (scenarioYearAccount) return scenarioYearAccount.amount

        return weightedAmount + induced
    }
    return weightedAmount + induced
}

/**
 * Retourne l'autofinancement pour une année donnée, qui a été définit comme la
 * CAF Brute par le client.
 * @param observations
 * @param year
 */
export function getSelfFinancingAmount(
    observations: Model.Observation[],
    year: number
): number {
    return CashFlowDomain.getGrossCashFlowAmount(observations, year)
}

/**
 * Retourne la liste d'autofinancement pour les années spécifiées à partir de la
 * liste d'observations spécifiée en paramètre.
 * @param observations
 * @param years
 */
export function getSelfFinancingAmounts(
    observations: Model.Observation[],
    years: number[]
): number[] {
    return CashFlowDomain.getGrossCashFlowAmounts(observations, years)
}



// WEBPACK FOOTER //
// ./src/domain/sfp/prospective-domain.ts